import { LOYALTY_PROGRAMS_BY_ID } from '../data/loyaltyPrograms';

// Airline name → loyalty program ID mapping
const AIRLINE_PROGRAM_MAP = {
  'Emirates': 'emirates-skywards',
  'Qatar Airways': null,
  'Etihad': null,
  'Turkish Airlines': 'lufthansa-miles-more',
  'Lufthansa': 'lufthansa-miles-more',
  'British Airways': 'british-avios',
  'United Airlines': 'united-mileageplus',
  'Delta Air Lines': 'delta-skymiles',
  'American Airlines': 'aa-aadvantage',
  'Southwest Airlines': 'southwest-rapidrewards',
  'Singapore Airlines': 'singapore-krisflyer',
  'Alaska Airlines': 'alaska-mileageplan',
  'Air France': 'air-france-flying-blue',
  'ANA': null,
};

// Bank → known card families with earn rates for travel
const BANK_CARD_EARN_RATES = {
  Chase: { category: 'travel', rate: 3, cpp: 1.5, pointsName: 'Ultimate Rewards' },
  Amex: { category: 'travel', rate: 5, cpp: 1.0, pointsName: 'Membership Rewards' },
  Citi: { category: 'travel', rate: 3, cpp: 1.0, pointsName: 'ThankYou Points' },
  'Capital One': { category: 'travel', rate: 5, cpp: 1.0, pointsName: 'Venture Miles' },
};

// Approximate miles needed per cabin class for a typical long-haul flight
const MILES_NEEDED_BY_CABIN = {
  Economy: 60000,
  'Premium Economy': 80000,
  Business: 120000,
  First: 180000,
};

/**
 * Calculate smart savings for a flight based on user's cards, loyalty programs, and transfer partners.
 *
 * @param {Object} flight - { airline, price, cabin, ... }
 * @param {Array} cards - user's cards from storageService
 * @param {Array} loyaltyPrograms - from useLoyalty() context (enrolled programs with balances)
 * @param {Object} benefitsData - from benefitsService (card benefits) - can be null
 * @param {Array} transferPartners - from transferPartners.js
 * @returns {Object} savings breakdown
 */
export function calculateSmartSavings(flight, cards, loyaltyPrograms, benefitsData, transferPartners) {
  const bestCard = findBestEarningCard(flight, cards, benefitsData);
  const loyaltyMatch = findLoyaltyMatch(flight, loyaltyPrograms);
  const transferOpps = findTransferOpportunities(flight, cards, transferPartners);
  const travelCredits = findTravelCredits(cards, benefitsData);

  // Build strategies ranked by savings
  const strategies = [];

  if (bestCard) {
    strategies.push({
      type: 'pay-with-card',
      label: `Pay with ${bestCard.cardName}`,
      savings: bestCard.pointValue,
      detail: `Earn ${bestCard.earnRate} points`,
    });
  }

  for (const opp of transferOpps) {
    strategies.push({
      type: 'transfer-miles',
      label: `Transfer ${opp.bank} → ${opp.airline}`,
      savings: opp.estimatedValue,
      detail: `${opp.pointsAvailable.toLocaleString()} ${opp.bank} pts → ${opp.airline} miles`,
    });
  }

  if (loyaltyMatch?.canRedeem) {
    strategies.push({
      type: 'loyalty-redeem',
      label: `Use ${loyaltyMatch.programName}`,
      savings: flight.price,
      detail: `${loyaltyMatch.milesNeeded.toLocaleString()} miles for this flight`,
    });
  }

  for (const credit of travelCredits) {
    if (credit.applicable) {
      const creditAmount = parseCreditAmount(credit.credit);
      if (creditAmount > 0) {
        strategies.push({
          type: 'travel-credit',
          label: `${credit.card} credit`,
          savings: creditAmount,
          detail: credit.credit,
        });
      }
    }
  }

  // Sort by savings descending
  strategies.sort((a, b) => b.savings - a.savings);

  const totalSavings = strategies.length > 0 ? strategies[0].savings : 0;

  return {
    totalSavings,
    bestCard,
    loyaltyMatch,
    transferOpportunities: transferOpps,
    travelCredits,
    strategies,
  };
}

function findBestEarningCard(flight, cards, benefitsData) {
  if (!cards || cards.length === 0) return null;

  let best = null;
  let bestValue = 0;

  for (const card of cards) {
    const cardName = card.cardName || card.name || '';
    const bank = detectBank(cardName);
    const bankInfo = BANK_CARD_EARN_RATES[bank];

    if (!bankInfo) continue;

    // Check benefitsData for specific earn rates
    let rate = bankInfo.rate;
    let cpp = bankInfo.cpp;

    if (benefitsData?.cards) {
      const cardBenefits = benefitsData.cards.find(
        (c) => c.cardName === cardName || c.id === card.id
      );
      if (cardBenefits?.earnRates?.travel) {
        rate = cardBenefits.earnRates.travel;
      }
      if (cardBenefits?.cpp) {
        cpp = cardBenefits.cpp;
      }
    }

    const pointsEarned = Math.round(flight.price * rate);
    const pointValue = (pointsEarned * cpp) / 100;

    if (pointValue > bestValue) {
      bestValue = pointValue;
      best = {
        cardName,
        earnRate: `${rate}x`,
        category: 'travel',
        pointsEarned,
        pointValue: Math.round(pointValue * 100) / 100,
      };
    }
  }

  return best;
}

function findLoyaltyMatch(flight, loyaltyPrograms) {
  if (!loyaltyPrograms || loyaltyPrograms.length === 0) return null;

  const programId = AIRLINE_PROGRAM_MAP[flight.airline];
  if (!programId) return null;

  const enrolled = loyaltyPrograms.find((p) => p.programId === programId);
  if (!enrolled) return null;

  const programInfo = LOYALTY_PROGRAMS_BY_ID.get(programId);
  if (!programInfo) return null;

  const milesNeeded = MILES_NEEDED_BY_CABIN[flight.cabin] || 60000;
  const canRedeem = enrolled.balance >= milesNeeded;

  return {
    programName: programInfo.name,
    currentBalance: enrolled.balance,
    milesNeeded,
    canRedeem,
    partialValue: canRedeem ? null : Math.round((enrolled.balance * programInfo.cpp) / 100),
  };
}

function findTransferOpportunities(flight, cards, transferPartners) {
  if (!cards || cards.length === 0 || !transferPartners) return [];

  const opportunities = [];

  // Find transfer partners that match this airline
  const matchingPartners = transferPartners.filter(
    (tp) => tp.airline === flight.airline || flight.airline?.includes(tp.airline?.split('/')[0])
  );

  for (const partner of matchingPartners) {
    // Check if user has a card from this bank
    const userCard = cards.find((c) => {
      const cardName = c.cardName || c.name || '';
      return detectBank(cardName) === partner.bank;
    });

    if (!userCard) continue;

    const bankInfo = BANK_CARD_EARN_RATES[partner.bank];
    const milesNeeded = MILES_NEEDED_BY_CABIN[flight.cabin] || 60000;
    // Estimate points available (rough estimate based on typical balances)
    const estimatedPoints = 50000 + Math.round(Math.random() * 50000);
    const cpp = bankInfo?.cpp || 1.0;

    opportunities.push({
      bank: partner.bank,
      card: userCard.cardName || userCard.name,
      airline: partner.airline,
      ratio: partner.ratio,
      pointsAvailable: estimatedPoints,
      bonus: partner.bonus,
      estimatedValue: Math.round((milesNeeded * cpp) / 100),
    });
  }

  return opportunities;
}

function findTravelCredits(cards, benefitsData) {
  if (!cards || cards.length === 0) return [];

  const credits = [];

  for (const card of cards) {
    const cardName = card.cardName || card.name || '';
    const lower = cardName.toLowerCase();

    // Check benefitsData for specific credits
    if (benefitsData?.cards) {
      const cardBenefits = benefitsData.cards.find(
        (c) => c.cardName === cardName || c.id === card.id
      );
      if (cardBenefits?.travelCredit) {
        credits.push({
          card: cardName,
          credit: cardBenefits.travelCredit,
          applicable: true,
        });
        continue;
      }
    }

    // Fallback: detect well-known cards with travel credits
    if (lower.includes('platinum') && lower.includes('amex')) {
      credits.push({ card: cardName, credit: '$200 airline credit', applicable: true });
    } else if (lower.includes('sapphire reserve')) {
      credits.push({ card: cardName, credit: '$300 travel credit', applicable: true });
    } else if (lower.includes('venture x')) {
      credits.push({ card: cardName, credit: '$300 travel credit', applicable: true });
    }
  }

  return credits;
}

function detectBank(cardName) {
  const lower = (cardName || '').toLowerCase();
  if (lower.includes('chase') || lower.includes('sapphire') || lower.includes('freedom')) return 'Chase';
  if (lower.includes('amex') || lower.includes('american express') || lower.includes('platinum') || lower.includes('gold card')) return 'Amex';
  if (lower.includes('citi')) return 'Citi';
  if (lower.includes('capital one') || lower.includes('venture')) return 'Capital One';
  return '';
}

function parseCreditAmount(creditStr) {
  const match = creditStr?.match(/\$(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
